/* ============================================================
   egy-cx-quiz-tracker.js — fire-and-forget event helper for egy-cx-quiz (same edge function, prefixed event names)
   Sinks: GA4 (gtag) · Klaviyo (_learnq, Q7 only) · Supabase edge fn
   All sinks no-op gracefully if their global is missing.
   ============================================================ */
(function () {
  "use strict";

  // === Endpoint for owned-data sink ===
  const TRACKER_ENDPOINT = "https://hnoohveebhibozqcjkxz.supabase.co/functions/v1/cx-quiz-tracker";
  // Anonymous public key (publishable; OK in client bundle)
  const SUPABASE_ANON = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imhub29odmVlYmhpYm96cWNqa3h6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzAzMjA0MzgsImV4cCI6MjA0NTg5NjQzOH0.n4qNWpZPPb2g-Dpkp6sZO4ci19GDEJ9tpqOEp_8f2Is";

  // === Session context (captured once) ===
  const SESSION_KEY = "egy_cx_quiz_session";
  const STARTED_KEY = "egy_cx_quiz_started_ts";
  const RESULTS_VIEWED_KEY = "egy_cx_quiz_results_viewed";

  function getSessionId() {
    try {
      let sid = sessionStorage.getItem(SESSION_KEY);
      if (!sid) {
        sid = (crypto && crypto.randomUUID) ? crypto.randomUUID() : fallbackUuid();
        sessionStorage.setItem(SESSION_KEY, sid);
      }
      return sid;
    } catch (e) { return fallbackUuid(); }
  }

  function fallbackUuid() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0;
      return (c === "x" ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }

  function getStartTime() {
    try {
      let t = sessionStorage.getItem(STARTED_KEY);
      if (!t) { t = String(Date.now()); sessionStorage.setItem(STARTED_KEY, t); }
      return parseInt(t, 10);
    } catch (e) { return Date.now(); }
  }

  // === URL params captured once per session ===
  function getUrlContext() {
    try {
      const cached = sessionStorage.getItem("egy_cx_quiz_ctx");
      if (cached) return JSON.parse(cached);
    } catch (e) {}
    const params = new URLSearchParams(window.location.search || "");
    const ctx = {
      utm_source: params.get("utm_source") || null,
      utm_medium: params.get("utm_medium") || null,
      utm_campaign: params.get("utm_campaign") || null,
      utm_term: params.get("utm_term") || null,
      utm_content: params.get("utm_content") || null,
      fbclid: params.get("fbclid") || null,
      gclid: params.get("gclid") || null,
      ttclid: params.get("ttclid") || null,
      referrer: document.referrer || null,
      user_agent: navigator.userAgent || null,
      device_type: detectDevice()
    };
    try { sessionStorage.setItem("egy_cx_quiz_ctx", JSON.stringify(ctx)); } catch (e) {}
    return ctx;
  }

  function detectDevice() {
    const ua = navigator.userAgent || "";
    if (/iPad/i.test(ua) || /Tablet/i.test(ua)) return "tablet";
    if (/Mobi|Android|iPhone/i.test(ua)) return "mobile";
    return "desktop";
  }

  // === Main tracker ===
  function cxTrack(eventName, payload) {
    const session_id = getSessionId();
    const ctx = getUrlContext();
    const cumulative_time_ms = Date.now() - getStartTime();

    const body = Object.assign({
      session_id,
      event_name: eventName,
      cumulative_time_ms
    }, ctx, payload || {});

    // Mark results_viewed for abandon detection
    if (eventName === "egy_cx_quiz_results_viewed") {
      try { sessionStorage.setItem(RESULTS_VIEWED_KEY, "1"); } catch (e) {}
    }

    // Sink A — GA4
    try {
      if (window.gtag) {
        const gaPayload = Object.assign({}, payload || {}, { session_id });
        // GA4 has 100-char limit on param values; truncate long JSON
        Object.keys(gaPayload).forEach(k => {
          if (typeof gaPayload[k] === "object") gaPayload[k] = JSON.stringify(gaPayload[k]).slice(0, 100);
        });
        window.gtag("event", eventName, gaPayload);
      }
    } catch (e) { console.warn("[cx-quiz] gtag fail:", e); }

    // Sink B — Supabase edge function (owned data)
    try {
      fetch(TRACKER_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "apikey": SUPABASE_ANON,
          "Authorization": "Bearer " + SUPABASE_ANON
        },
        body: JSON.stringify(body),
        keepalive: true   // survives page unload (critical for egy_cx_quiz_abandoned)
      }).catch(err => console.warn("[cx-quiz] tracker POST fail:", err));
    } catch (e) { console.warn("[cx-quiz] tracker fetch fail:", e); }

    // Sink C — Klaviyo (Q7 email_captured only)
    if (eventName === "egy_cx_quiz_email_captured" && payload && payload.email) {
      try {
        if (window._learnq) {
          window._learnq.push(["identify", {
            $email: payload.email,
            cx_quiz_completed: true,
            cx_quiz_recommended_stack: payload.recommended_stack || "sleep+essentials+energy+beauty"
          }]);
          window._learnq.push(["track", "Quiz Completed", {
            quiz: "cx-quiz",
            session_id: session_id,
            recommended_stack: payload.recommended_stack || "sleep+essentials+energy+beauty"
          }]);
        }
      } catch (e) { console.warn("[cx-quiz] klaviyo fail:", e); }
    }

    if (window.CX_QUIZ_DEBUG) console.log("[cx-quiz] track →", eventName, body);
  }

  // Expose
  window.cxTrack = cxTrack;
  window.cxQuizSessionId = getSessionId;
  window.cxQuizResultsViewed = () => {
    try { return sessionStorage.getItem(RESULTS_VIEWED_KEY) === "1"; }
    catch (e) { return false; }
  };
})();
